import { Building2, ChevronLeft, ChevronRight, LoaderCircle, Search, SlidersHorizontal, Wifi, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Card, DataAge, EmptyState, ErrorState, Field, LoadingBlock, PageHeader, StatusBadge } from "../components/ui";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useRemote } from "../hooks/useRemote";
import { apiRequest, queryString } from "../lib/api";
import { CRITICALITY_LABELS, HEALTH_LABELS } from "../lib/format";
import type { BuildingListItem, Criticality, CursorPage, FiltersResponse, HealthState } from "../types";

export function BuildingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([]);
  const filters = useMemo(() => ({
    q: searchParams.get("q") ?? "",
    clientId: searchParams.get("clientId") ?? "",
    region: searchParams.get("region") ?? "",
    status: searchParams.get("status") ?? "",
    criticality: searchParams.get("criticality") ?? "",
  }), [searchParams]);
  const requestQuery = queryString({ search: filters.q, clientId: filters.clientId, region: filters.region, status: filters.status, criticality: filters.criticality, cursor, limit: 50 });
  const remote = useRemote(() => apiRequest<CursorPage<BuildingListItem>>(`/api/v1/buildings${requestQuery}`), [requestQuery], 30_000);
  const options = useRemote(() => apiRequest<FiltersResponse>("/api/v1/filters"), [], 300_000);

  useDocumentTitle("Edificios");

  const updateFilter = (name: string, value: string) => {
    setCursor(null);
    setCursorHistory([]);
    const next = new URLSearchParams(searchParams);
    value ? next.set(name, value) : next.delete(name);
    setSearchParams(next, { replace: true });
  };

  const [searchDraft, setSearchDraft, resetSearchDraft] = useDebouncedSearch(filters.q, (value) => updateFilter("q", value));

  const resetFilters = () => {
    resetSearchDraft("");
    setSearchParams({}, { replace: true });
    setCursor(null);
    setCursorHistory([]);
  };

  const nextPage = () => {
    if (remote.loading || remote.error || !remote.data?.nextCursor) return;
    setCursorHistory((history) => [...history, cursor]);
    setCursor(remote.data.nextCursor);
  };

  const previousPage = () => {
    const previous = cursorHistory.at(-1);
    if (remote.loading || previous === undefined) return;
    setCursor(previous);
    setCursorHistory((history) => history.slice(0, -1));
  };

  const filterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="page">
      <PageHeader eyebrow="Inventario monitoreado" title="Edificios" description="Buscá y filtrá el portfolio sin perder de vista qué necesita atención." />
      <Card className="building-filters" aria-label="Filtros de edificios">
        <Field label="Buscar edificio">
          <span className="input-with-icon"><Search size={16} /><input value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} placeholder="Nombre, ciudad o código" /></span>
        </Field>
        <Field label="Cliente"><select value={filters.clientId} onChange={(event) => updateFilter("clientId", event.target.value)}><option value="">Todos</option>{options.data?.clients.map((client) => <option value={client.id} key={client.id}>{client.name}</option>)}</select></Field>
        <Field label="Región"><select value={filters.region} onChange={(event) => updateFilter("region", event.target.value)}><option value="">Todas</option>{options.data?.regions.map((region) => <option value={region.name} key={region.id}>{region.name}</option>)}</select></Field>
        <Field label="Estado"><select value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}><option value="">Todos</option>{(options.data?.statuses ?? ["outage", "degraded", "stale", "maintenance", "operational"]).map((status) => <option value={status} key={status}>{HEALTH_LABELS[status as HealthState]}</option>)}</select></Field>
        <Field label="Criticidad"><select value={filters.criticality} onChange={(event) => updateFilter("criticality", event.target.value)}><option value="">Todas</option>{(options.data?.criticalities ?? ["critical", "important", "standard"]).map((criticality) => <option value={criticality} key={criticality}>{CRITICALITY_LABELS[criticality as Criticality]}</option>)}</select></Field>
        <div className="filter-bar__tail"><span><SlidersHorizontal size={15} /> {filterCount ? `${filterCount} filtros activos` : "Sin filtros"}</span>{filterCount ? <Button variant="ghost" size="small" onClick={resetFilters}><X size={14} /> Limpiar</Button> : null}</div>
      </Card>

      <Card className="building-table-card" aria-busy={remote.loading}>
        {remote.loading && !remote.data ? <LoadingBlock rows={9} /> : null}
        {remote.error && !remote.data ? <ErrorState error={remote.error} onRetry={remote.reload} /> : null}
        {remote.data?.items.length === 0 ? <EmptyState icon={Building2} title="No encontramos edificios" description="Probá quitar algún filtro o buscar con otro nombre." action={<Button variant="secondary" onClick={resetFilters}>Limpiar filtros</Button>} /> : null}
        {remote.error && remote.data ? <div className="inline-error" role="alert">No se pudo actualizar la lista. Se muestran los últimos datos disponibles.</div> : null}
        {remote.loading && remote.data ? <p className="list-updating" role="status"><LoaderCircle size={14} className="spin" aria-hidden="true" /> Actualizando resultados…</p> : null}
        {remote.data?.items.length ? <BuildingTable items={remote.data.items} /> : null}
        {remote.data && (remote.data.nextCursor || cursorHistory.length > 0) ? (
          <nav className="pagination" aria-label="Paginación de edificios"><Button variant="secondary" size="small" disabled={remote.loading || cursorHistory.length === 0} onClick={previousPage}><ChevronLeft size={15} /> Anterior</Button><span>Página {cursorHistory.length + 1}</span><Button variant="secondary" size="small" disabled={remote.loading || Boolean(remote.error) || !remote.data.nextCursor} onClick={nextPage}>Siguiente <ChevronRight size={15} /></Button></nav>
        ) : null}
      </Card>
    </div>
  );
}

function BuildingTable({ items }: { items: BuildingListItem[] }) {
  return (
    <div className="virtual-table" role="table" aria-label="Edificios monitoreados">
      <div className="virtual-table__header building-grid" role="row">
        <span role="columnheader">Edificio</span><span role="columnheader">Estado</span><span role="columnheader">Conectividad</span><span role="columnheader">Grabador</span><span role="columnheader">Último dato</span>
      </div>
      <div className="virtual-table__viewport" role="rowgroup" tabIndex={0} aria-label={`${items.length} edificios en esta página`}>
        {items.map((building) => (
          <div className="virtual-table__row building-grid" role="row" key={building.id}>
            <span role="cell" className="building-cell"><span className="building-avatar"><Building2 size={18} /></span><span><Link to={`/edificios/${building.id}`}>{building.name}</Link><small>{building.client.name} · {building.city}</small></span></span>
            <span role="cell"><StatusBadge status={building.status} compact /></span>
            <span role="cell"><StatusBadge status={building.networkStatus} compact /></span>
            <span role="cell"><StatusBadge status={building.recorderStatus} compact /></span>
            <span role="cell" className="last-data"><Wifi size={14} /><DataAge value={building.lastSeenAt} /></span>
          </div>
        ))}
      </div>
    </div>
  );
}
